import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Modal from '../../components/common/Modal';
import Alert from '../../components/common/Alert';
import { getExamResults, exportCsv, exportPdf, getExam, overrideResult } from '../../api/admin.api';

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

const PassBadge = ({ passed }) => (
  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${
    passed ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'
  }`}>
    {passed ? 'PASS' : 'FAIL'}
  </span>
);

const SubmitBadge = ({ reason }) => {
  const auto = reason === 'tab_limit' || reason === 'timeout';
  return (
    <span className={`px-2 py-0.5 rounded-lg text-xs font-medium ${
      auto ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'
    }`}>
      {reason || 'manual'}
    </span>
  );
};

const SortTh = ({ label, field, sort, onSort, className = '' }) => {
  const active = sort.field === field;
  return (
    <th
      className={`px-4 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide cursor-pointer select-none hover:text-slate-700 ${className}`}
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className="text-slate-300">{active ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}</span>
      </span>
    </th>
  );
};

const ResultsList = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [overrideModal, setOverrideModal] = useState(null);
  const [overrideForm, setOverrideForm] = useState({ score: '', reason: '' });
  const [overrideError, setOverrideError] = useState('');
  const [overrideSaving, setOverrideSaving] = useState(false);

  // Search & sort
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'pass' | 'fail'
  const [sort, setSort] = useState({ field: 'percentage', dir: 'desc' });

  const loadResults = () => {
    Promise.all([getExam(id), getExamResults(id)])
      .then(([eRes, rRes]) => {
        setExam(eRes.data.data);
        setResults(rRes.data.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(loadResults, [id]);

  const openOverride = (r) => {
    setOverrideForm({ score: r.override_score ?? r.score, reason: r.override_reason || '' });
    setOverrideError('');
    setOverrideModal({ resultId: r.id, totalMarks: r.total_marks });
  };

  const handleOverrideSave = async () => {
    setOverrideError('');
    setOverrideSaving(true);
    try {
      await overrideResult(overrideModal.resultId, { score: Number(overrideForm.score), reason: overrideForm.reason });
      setOverrideModal(null);
      loadResults();
    } catch (err) {
      setOverrideError(err.response?.data?.message || 'Failed to save override');
    } finally {
      setOverrideSaving(false);
    }
  };

  const handleCsv = async () => {
    const res = await exportCsv(id);
    downloadBlob(res.data, `results-exam-${id}.csv`);
  };

  const handlePdf = async () => {
    const res = await exportPdf(id);
    downloadBlob(res.data, `results-exam-${id}.pdf`);
  };

  const handleSort = (field) => {
    setSort((prev) => ({ field, dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc' }));
  };

  if (loading) return <AdminLayout><Spinner className="py-20" /></AdminLayout>;

  const passed = results.filter((r) => r.pass_status).length;
  const passRate = results.length ? Math.round((passed / results.length) * 100) : 0;
  const avgScore = results.length
    ? Math.round(results.reduce((s, r) => s + Number(r.percentage), 0) / results.length)
    : 0;

  // Filter
  const q = search.toLowerCase();
  const filtered = results.filter((r) => {
    const matchSearch = !q ||
      (r.candidate_name || '').toLowerCase().includes(q) ||
      (r.candidate_email || '').toLowerCase().includes(q) ||
      (r.candidate_college || '').toLowerCase().includes(q);
    const matchStatus = statusFilter === 'all' || (statusFilter === 'pass' ? r.pass_status : !r.pass_status);
    return matchSearch && matchStatus;
  });

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    if (sort.field === 'percentage' || sort.field === 'score' || sort.field === 'tab_switch_count') {
      return dir * (Number(a[sort.field]) - Number(b[sort.field]));
    }
    if (sort.field === 'pass_status') return dir * (a.pass_status === b.pass_status ? 0 : a.pass_status ? -1 : 1);
    if (sort.field === 'submitted_at') return dir * (new Date(a.submitted_at || 0) - new Date(b.submitted_at || 0));
    return dir * String(a[sort.field] || '').localeCompare(String(b[sort.field] || ''));
  });

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-start justify-between mb-7">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <button
              onClick={() => navigate(`/admin/exams/${id}`)}
              className="text-slate-400 hover:text-slate600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-slate-400 text-sm">{exam?.title}</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Results</h1>
          <p className="text-slate-500 text-sm mt-0.5">{results.length} submission{results.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={handleCsv}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export CSV
          </Button>
          <Button variant="secondary" onClick={handlePdf}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export PDF
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      {results.length > 0 && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4">
            <p className="text-xs text-slate-500 font-medium">Total Submissions</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{results.length}</p>
          </div>
          <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-4">
            <p className="text-xs text-slate-500 font-medium">Pass Rate</p>
            <p className="text-2xl font-bold text-emerald-700 mt-1">{passRate}%</p>
          </div>
          <div className="bg-blue-50 rounded-2xl border border-blue-200 p-4">
            <p className="text-xs text-slate-500 font-medium">Average Score</p>
            <p className="text-2xl font-bold text-blue-700 mt-1">{avgScore}%</p>
          </div>
        </div>
      )}

      {/* Search + Filter bar */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, college…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
        >
          <option value="all">All Results</option>
          <option value="pass">Pass Only</option>
          <option value="fail">Fail Only</option>
        </select>
        {(search || statusFilter !== 'all') && (
          <span className="text-xs text-slate-400">{sorted.length} of {results.length}</span>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <SortTh label="Candidate" field="candidate_name" sort={sort} onSort={handleSort} />
              <SortTh label="College" field="candidate_college" sort={sort} onSort={handleSort} />
              <SortTh label="Score" field="score" sort={sort} onSort={handleSort} />
              <SortTh label="%" field="percentage" sort={sort} onSort={handleSort} />
              <SortTh label="Result" field="pass_status" sort={sort} onSort={handleSort} />
              <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">C / W / —</th>
              <SortTh label="Tabs" field="tab_switch_count" sort={sort} onSort={handleSort} />
              <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Submit</th>
              <SortTh label="Time" field="submitted_at" sort={sort} onSort={handleSort} />
              <th className="px-4 py-3.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sorted.length === 0 && (
              <tr>
                <td colSpan={10} className="text-center py-14 text-slate-400">
                  <div className="flex flex-col items-center gap-2">
                    <span className="text-3xl">📋</span>
                    <span>{results.length === 0 ? 'No results yet' : 'No results match your filters'}</span>
                  </div>
                </td>
              </tr>
            )}
            {sorted.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3.5">
                  <p className="font-semibold text-slate-800">{r.candidate_name || '—'}</p>
                  <p className="text-slate-400 text-xs">{r.candidate_email}</p>
                </td>
                <td className="px-4 py-3.5 text-slate-500 text-xs">{r.candidate_college || '—'}</td>
                <td className="px-4 py-3.5 font-semibold text-slate-700">
                  {r.override_score != null ? (
                    <span title={`Original: ${r.score}. Reason: ${r.override_reason}`}>
                      <span className="text-amber-600">{r.override_score}</span>
                      <span className="text-slate-400">/{r.total_marks}</span>
                      <span className="ml-1 text-xs text-amber-500">*</span>
                    </span>
                  ) : (
                    <span>{r.score}/{r.total_marks}</span>
                  )}
                </td>
                <td className="px-4 py-3.5">
                  <span className={`font-semibold ${Number(r.percentage) >= 50 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {r.percentage}%
                  </span>
                </td>
                <td className="px-4 py-3.5"><PassBadge passed={r.pass_status} /></td>
                <td className="px-4 py-3.5 text-slate-500 font-mono text-xs">
                  <span className="text-emerald-600">{r.correct}</span>
                  {' / '}
                  <span className="text-red-500">{r.wrong}</span>
                  {' / '}
                  <span className="text-slate-400">{r.unanswered}</span>
                </td>
                <td className="px-4 py-3.5">
                  <span className={`font-semibold ${r.tab_switch_count > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                    {r.tab_switch_count}
                  </span>
                </td>
                <td className="px-4 py-3.5"><SubmitBadge reason={r.submit_reason} /></td>
                <td className="px-4 py-3.5 text-slate-400 text-xs whitespace-nowrap">
                  {r.submitted_at ? new Date(r.submitted_at).toLocaleString() : '—'}
                </td>
                <td className="px-4 py-3.5">
                  <Button size="sm" variant="ghost" onClick={() => openOverride(r)}>Override</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Override Modal */}
      <Modal isOpen={!!overrideModal} onClose={() => setOverrideModal(null)} title="Override Score">
        {overrideModal && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                New Score <span className="text-slate-400 text-xs">(0 – {overrideModal.totalMarks})</span>
              </label>
              <input
                type="number"
                min={0}
                max={overrideModal.totalMarks}
                step={0.5}
                value={overrideForm.score}
                onChange={(e) => setOverrideForm({ ...overrideForm, score: e.target.value })}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Reason <span className="text-red-500">*</span></label>
              <textarea
                rows={3}
                value={overrideForm.reason}
                onChange={(e) => setOverrideForm({ ...overrideForm, reason: e.target.value })}
                placeholder="Explain why the score is being adjusted…"
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              />
            </div>
            {overrideError && <Alert type="error">{overrideError}</Alert>}
            <div className="flex gap-2 justify-end pt-1">
              <Button variant="secondary" onClick={() => setOverrideModal(null)}>Cancel</Button>
              <Button onClick={handleOverrideSave} loading={overrideSaving} disabled={!overrideForm.reason.trim()}>
                Save Override
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </AdminLayout>
  );
};

export default ResultsList;
